// src/components/CookieBanner.jsx
// First-visit cookie consent banner (en / fr / de / ar, RTL aware).
// Saves to localStorage("cookie_prefs") – same key used by /cookies page and ad components.
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { updateConsent, updateAdsConsent } from "../analytics";
import { hasChosenLang, onLangChosen } from "../lib/langChoice";

const API_BASE = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000/api";
const LANGS = ["en", "fr", "de", "ar"];

const TEXT = {
  en: {
    title: "We value your privacy",
    body: "We use cookies to run the site, analyse traffic and show ads. You can accept all, reject non-essential cookies, or choose what to allow.",
    accept: "Accept all",
    reject: "Reject non-essential",
    custom: "Customize",
  },
  fr: {
    title: "Nous respectons votre vie privée",
    body: "Nous utilisons des cookies pour faire fonctionner le site, analyser le trafic et afficher des publicités. Vous pouvez tout accepter, refuser les cookies non essentiels ou choisir.",
    accept: "Tout accepter",
    reject: "Refuser le non essentiel",
    custom: "Personnaliser",
  },
  de: {
    title: "Wir respektieren Ihre Privatsphäre",
    body: "Wir verwenden Cookies, um die Website zu betreiben, den Traffic zu analysieren und Werbung anzuzeigen. Sie können alle akzeptieren, nicht notwendige ablehnen oder selbst wählen.",
    accept: "Alle akzeptieren",
    reject: "Nicht notwendige ablehnen",
    custom: "Anpassen",
  },
  ar: {
    title: "نحترم خصوصيتك",
    body: "نستخدم ملفات تعريف الارتباط لتشغيل الموقع وتحليل الزيارات وعرض الإعلانات. يمكنك قبول الكل أو رفض غير الضرورية أو اختيار ما تسمح به.",
    accept: "قبول الكل",
    reject: "رفض غير الضرورية",
    custom: "تخصيص",
  },
};

function hasChoice() {
  try {
    return !!localStorage.getItem("cookie_prefs");
  } catch {
    return true; // storage unavailable: don't nag
  }
}

function getToken() {
  try {
    return localStorage.getItem("accessToken") || localStorage.getItem("token") || null;
  } catch {
    return null;
  }
}

export default function CookieBanner() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(() => !hasChoice());
  // show the cookie banner only after the language has been chosen
  const [langDone, setLangDone] = useState(hasChosenLang);
  useEffect(() => onLangChosen(() => setLangDone(hasChosenLang())), []);

  const first = pathname.split("/")[1];
  const lang = LANGS.includes(first) ? first : "en";
  const tx = TEXT[lang];
  const isRTL = lang === "ar";

  // hide if the choice gets saved elsewhere (e.g. /cookies page or another tab)
  useEffect(() => {
    const sync = () => setVisible(!hasChoice());
    window.addEventListener("cookie-prefs-changed", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("cookie-prefs-changed", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const save = useCallback(async (prefs) => {
    try {
      localStorage.setItem("cookie_prefs", JSON.stringify(prefs));
    } catch { /* ignore */ }
    updateConsent(prefs.analytics);
    updateAdsConsent(prefs.marketing);
    window.dispatchEvent(new Event("cookie-prefs-changed"));
    setVisible(false);

    // best effort: store server-side only for logged-in users
    const token = getToken();
    if (token) {
      try {
        await axios.post(`${API_BASE}/cookie-preferences/`, prefs, {
          headers: {
            "Content-Type": "application/json",
            Authorization: token.startsWith("Bearer ") ? token : `Bearer ${token}`,
          },
        });
      } catch { /* local choice already saved */ }
    }
  }, []);

  const onCookiesPage = /^\/(cookies|privacy|terms)\/?$/.test(pathname);
  const isAdmin = pathname.startsWith("/admin");
  if (!visible || !langDone || onCookiesPage || isAdmin) return null;

  return (
    <div
      className="ckb-root"
      role="dialog"
      aria-live="polite"
      aria-label={tx.title}
      dir={isRTL ? "rtl" : "ltr"}
    >
      <style>{CSS}</style>
      <div className="ckb-text">
        <strong className="ckb-title">{tx.title}</strong>
        <p className="ckb-body">{tx.body}</p>
      </div>
      <div className="ckb-actions">
        <button
          type="button"
          className="ckb-btn ckb-btn--primary"
          onClick={() => save({ analytics: true, preferences: true, marketing: true })}
        >
          {tx.accept}
        </button>
        <button
          type="button"
          className="ckb-btn"
          onClick={() => save({ analytics: false, preferences: false, marketing: false })}
        >
          {tx.reject}
        </button>
        <button type="button" className="ckb-btn ckb-btn--link" onClick={() => navigate("/cookies")}>
          {tx.custom}
        </button>
      </div>
    </div>
  );
}

const CSS = `
.ckb-root{
  position:fixed;left:0;right:0;bottom:0;z-index:2147483000;
  display:flex;flex-wrap:wrap;align-items:center;justify-content:space-between;gap:12px 24px;
  padding:16px 20px;background:#111;color:#f1f1f1;
  box-shadow:0 -4px 20px rgba(0,0,0,.35);
  font-family:'DM Sans','Noto Sans Arabic',system-ui,sans-serif;font-size:14px;line-height:1.5;
}
.ckb-text{flex:1 1 380px;min-width:0;}
.ckb-title{display:block;font-size:15px;margin-bottom:2px;}
.ckb-body{margin:0;opacity:.85;}
.ckb-actions{display:flex;flex-wrap:wrap;gap:8px;align-items:center;}
.ckb-btn{
  cursor:pointer;border:1px solid #666;background:transparent;color:#fff;
  padding:8px 16px;border-radius:6px;font:inherit;font-weight:600;
}
.ckb-btn:hover{background:#2a2a2a;}
.ckb-btn--primary{background:#e11d2e;border-color:#e11d2e;}
.ckb-btn--primary:hover{background:#c4111f;}
.ckb-btn--link{border-color:transparent;text-decoration:underline;}
@media(max-width:600px){
  .ckb-actions{width:100%;}
  .ckb-btn{flex:1 1 auto;}
}
`;
